import type { KibTagThemes } from '@chewy/kib-tag-react'
import type { Constant } from '@pbt/pbt-ui-components'
import { DateUtils, moment } from '@pbt/pbt-ui-components'

import { RxPrescriptionOrigin, RxType } from '~/api/graphql/generated/types'
import { getHighValue } from '~/components/dashboard/invoices/invoiceUtils'
import { ColorVariant, ColorVariantToTheme } from '~/constants/colors'
import { PrescriptionStates, PrescriptionType } from '~/constants/prescription'
import { OrderType } from '~/constants/SOAPStates'
import i18n from '~/locales/i18n'
import type { Prescription } from '~/types/entities/prescriptions'
import {
  getPrescriptionAutoshipFrequency,
  getPrescriptionAutoshipUnitId,
} from '~/utils/prescription'
import { getRemainingRefillsCount } from '~/utils/prescriptionUtils'

import type { OrderTableRow } from './components/OrderTable'

type NullableType<T> = T | null | undefined
type NullableString = NullableType<string>
type NullablePrescriptionType = NullableType<PrescriptionType>

export type DisplayField = {
  boldValue?: boolean
  label: string
  onClick?: () => void
  parentPrescription?: Prescription
  prescription?: Prescription
  relativeTime?: string | null
  tooltipText?: string
  value: string
}

export interface PrescriptionFlags {
  isCanceled: boolean
  isChewy: boolean
  isDeclined: boolean
  isPendingVetApproval: boolean
  isScriptOut: boolean
  stateName: string | null
}

export interface GetRxFieldsOptions {
  autoshipUnit?: Constant[]
  onDispenseRefill?: () => void
  prescriptionProductSizeUnit?: Constant[]
  prescriptionStates?: Constant[]
  productSizeUnit?: Constant[]
}

type OrderTableFieldRowOptions = Pick<
  GetRxFieldsOptions,
  | 'autoshipUnit'
  | 'prescriptionProductSizeUnit'
  | 'productSizeUnit'
  | 'prescriptionStates'
>

export const getPrescriptionFlags = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  prescriptionStates?: Constant[],
): PrescriptionFlags => {
  const stateName =
    rx.stateId && prescriptionStates
      ? (prescriptionStates.find((s) => s.id === rx.stateId)?.name ?? null)
      : null

  const isDeclined = stateName === PrescriptionStates.DECLINED
  const isCanceled = stateName === PrescriptionStates.CANCELED
  const isPendingVetApproval =
    stateName === PrescriptionStates.PENDING_VET_APPROVAL

  return {
    stateName,
    isDeclined,
    isCanceled,
    isPendingVetApproval,
    isScriptOut: prescriptionType === PrescriptionType.SCRIPT_OUT,
    isChewy: prescriptionType === PrescriptionType.CHEWY,
  }
}

const DateFormatters = {
  date: (value: NullableString): string =>
    DateUtils.formatDate(value, false, 'MM/DD/YYYY'),
  dateTime: (value: NullableString): string =>
    DateUtils.formatDate(value, false, 'MM/DD/YYYY | h:mmA'),
}

export const computeRelativeTime = (
  date: NullableString,
  timezone: string,
): string | null => {
  if (!date) return null
  const m = moment.tz(date, timezone)
  return m.isValid() ? m.fromNow() : null
}

const isPrescriptionFilled = (rx: Prescription): boolean =>
  Boolean(rx.filledBy ?? rx.filledById)

const getFillDate = (rx: Prescription): string | null => {
  if (isPrescriptionFilled(rx)) {
    return rx.modificationDate || rx.updatedAt || rx.creationDate || null
  }
  return null
}

const PrescriptionFields = {
  expirationDate: (rx: Prescription): NullableString => {
    const drugExpirationDate =
      'drugExpirationDate' in rx
        ? (rx as { drugExpirationDate?: string }).drugExpirationDate
        : undefined
    return (
      rx.quantityDrugExpirationDate ??
      drugExpirationDate ??
      rx.refillExpirationDate ??
      null
    )
  },

  lastFillDate: (rx: Prescription): string | null => {
    const all = [rx, ...(rx.refills ?? [])]
    const dates = all
      .filter(isPrescriptionFilled)
      .map(getFillDate)
      .filter((date): date is string => date !== null)

    return dates.length > 0
      ? dates.reduce((a, b) => (a > b ? a : b), dates[0])
      : null
  },

  numberOfRefills: (rx: Prescription): number | null =>
    rx.quantityNumbRefills ?? null,

  refillsRemaining: (rx: Prescription): number => getRemainingRefillsCount(rx),
}

const toStringOrZero = (value: number | null | undefined): string =>
  value === null || value === undefined ? '0' : String(value)

const AUTOSHIP_UNIT_WEEK = 'Week'
const AUTOSHIP_UNIT_MONTH = 'Month'
const REFILL_SEPARATOR = ', '

export const formatSupplyDuration = (
  autoshipFrequency: number | undefined,
  autoshipUnitName: string | undefined,
): string | null => {
  if (!autoshipFrequency || !autoshipUnitName) return null

  if (autoshipUnitName === AUTOSHIP_UNIT_MONTH) {
    return `${autoshipFrequency} ${i18n.t('Constants:TIME_UNITS.MONTH_SHORT')}.`
  }

  if (autoshipUnitName === AUTOSHIP_UNIT_WEEK) {
    return `${autoshipFrequency}-${i18n.t('Constants:TIME_UNITS.WEEK_SHORT')}s.`
  }

  return null
}

/**
 * Gets the supply duration string for a prescription.
 * Returns formatted string like "6 mo." or "12-wks." or null if supply info is not available.
 * Uses autoship data from prescription or retailOrderLineItem.
 */
const getSupplyDuration = (
  rx: Prescription,
  autoshipUnit: Constant[],
): string | null => {
  const autoshipFrequency = getPrescriptionAutoshipFrequency(rx)
  const autoshipUnitId = getPrescriptionAutoshipUnitId(rx)

  if (!autoshipFrequency || !autoshipUnitId) {
    return null
  }

  const autoshipUnitObj = autoshipUnit.find((u) => u.id === autoshipUnitId)

  if (!autoshipUnitObj?.name) {
    return null
  }

  return formatSupplyDuration(autoshipFrequency, autoshipUnitObj.name)
}

interface QuantityRequestedOptions {
  autoshipUnit: Constant[]
  prescriptionProductSizeUnit: Constant[]
}

/**
 * Formats quantity and refills text matching QuantityAndRefills component logic.
 * Used for the "Qty Requested" field in declined/pending vet approval prescriptions.
 */
const getQuantityRequested = (
  rx: Prescription,
  { autoshipUnit, prescriptionProductSizeUnit }: QuantityRequestedOptions,
): string => {
  const quantity = rx.totalQuantity ?? getHighValue(rx.quantity) ?? 0

  const unitOfProduct = prescriptionProductSizeUnit.find(
    (unit) => unit.id === rx.totalQuantityUnitId,
  )

  const quantityText = unitOfProduct?.name
    ? `${quantity} ${unitOfProduct.name}`
    : String(quantity)

  const supplyDuration = getSupplyDuration(rx, autoshipUnit)
  const supplyText = supplyDuration ? ` (${supplyDuration} supply)` : ''

  const { quantityNumbRefills } = rx
  const refillText =
    quantityNumbRefills && quantityNumbRefills > 0
      ? `${REFILL_SEPARATOR}${i18n.t(
          'Plurals:Z_ICU_WORKAROUND.NUMBER_REFILLS',
          {
            number: quantityNumbRefills,
          },
        )}`
      : `${REFILL_SEPARATOR}${i18n.t('Clients:TIMELINE.PRESCRIPTION_CARD.NO_REFILLS')}`

  return `${quantityText}${supplyText}${refillText}`
}

const getFulfillmentText = (prescriptionType: PrescriptionType): string => {
  switch (prescriptionType) {
    case PrescriptionType.CHEWY:
      return i18n.t('Common:CHEWY_COM')
    case PrescriptionType.IN_HOUSE:
      return i18n.t('Common:IN_HOUSE')
    case PrescriptionType.SCRIPT_OUT:
      return i18n.t('Common:OUTSIDE_PHARMACY')
    default:
      return ''
  }
}

const getLastFill = (rx: Prescription): string => {
  const lastFillDate = PrescriptionFields.lastFillDate(rx)
  return DateFormatters.date(lastFillDate)
}

const buildFields = (
  conditionalFields: Array<DisplayField | null | false>,
): DisplayField[] => conditionalFields.filter(Boolean) as DisplayField[]

const getDeclinedFields = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  opts: GetRxFieldsOptions,
): DisplayField[] => {
  const { prescriptionProductSizeUnit = [], autoshipUnit = [] } = opts

  return buildFields([
    rx.creationDate
      ? {
          label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ORDER_DATE'),
          value: DateFormatters.dateTime(rx.creationDate),
        }
      : null,

    prescriptionType
      ? {
          label: i18n.t('Common:FULFILLMENT'),
          value: getFulfillmentText(prescriptionType),
        }
      : null,

    prescriptionProductSizeUnit.length > 0
      ? {
          label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.QTY_REQUESTED'),
          value: getQuantityRequested(rx, {
            autoshipUnit,
            prescriptionProductSizeUnit,
          }),
        }
      : null,
  ])
}

const getExpirationField = (
  rx: Prescription,
  isChewy: boolean,
): DisplayField | null => {
  const expirationDate = PrescriptionFields.expirationDate(rx)
  if (!expirationDate && !isChewy) return null
  return {
    label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.EXPIRATION'),
    value: DateFormatters.date(expirationDate),
  }
}

const getStandardFields = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  opts?: GetRxFieldsOptions,
): DisplayField[] => {
  const isScriptOut = prescriptionType === PrescriptionType.SCRIPT_OUT
  const isChewy = prescriptionType === PrescriptionType.CHEWY

  return buildFields([
    getExpirationField(rx, isChewy),

    prescriptionType
      ? {
          label: i18n.t('Common:FULFILLMENT'),
          value: getFulfillmentText(prescriptionType),
        }
      : null,

    {
      label: i18n.t(
        'Dialogs:PRESCRIPTION_DIALOG.QUANTITY_SECTION.REFILLS_QUANTITY',
      ),
      value: toStringOrZero(PrescriptionFields.numberOfRefills(rx)),
    },

    !isScriptOut && {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.REFILLS_REMAINING'),
      value: toStringOrZero(PrescriptionFields.refillsRemaining(rx)),
      onClick: opts?.onDispenseRefill,
      tooltipText: opts?.onDispenseRefill
        ? i18n.t('Common:DISPENSE_REFILL')
        : undefined,
    },

    !isScriptOut && prescriptionType
      ? {
          boldValue: !getLastFill(rx),
          label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.LAST_FILL'),
          value:
            getLastFill(rx) ||
            i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.IN_PROGRESS'),
        }
      : null,
  ])
}

export const getRxFields = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  options: GetRxFieldsOptions = {},
): DisplayField[] => {
  const { prescriptionStates } = options

  const { isDeclined, isCanceled, isPendingVetApproval } = getPrescriptionFlags(
    rx,
    prescriptionType,
    prescriptionStates,
  )

  const showDeclinedFields = (isDeclined || isPendingVetApproval) && !isCanceled

  return showDeclinedFields
    ? getDeclinedFields(rx, prescriptionType, options)
    : getStandardFields(rx, prescriptionType, options)
}

export const getInstructionsField = (rx: Prescription): DisplayField => ({
  label: i18n.t('Common:INSTRUCTION_PRINTED_ON_LABEL'),
  value: rx.notes || '',
})

export const getPrescriptionStateName = (
  rx: Prescription,
  prescriptionStates?: Constant[],
): string | null => getPrescriptionFlags(rx, null, prescriptionStates).stateName

/**
 * Checks if a prescription is a vet diet prescription.
 */
export const isVetDietPrescription = (prescription: Prescription): boolean =>
  prescription.rxType === RxType.VetDiet

/**
 * Checks if a field label matches the QTY_REQUESTED label.
 */
export const isQtyRequestedField = (
  fieldLabel: string,
  qtyRequestedLabel: string,
): boolean => fieldLabel === qtyRequestedLabel

export const getOrderFillStatusText = (rx: Prescription): string => {
  if (isPrescriptionFilled(rx)) {
    const fillDate = getFillDate(rx)
    const formattedDate = DateFormatters.date(fillDate)
    return i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_STATUS_COMPLETED', {
      date: formattedDate,
    })
  }
  return i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_STATUS_TODO')
}

const PrescriptionStateToColorVariant: Record<string, ColorVariant> = {
  [PrescriptionStates.PRESCRIBED]: ColorVariant.SUCCESS,
  [PrescriptionStates.APPROVED]: ColorVariant.SUCCESS,
  [PrescriptionStates.CANCELED]: ColorVariant.ERROR,
  [PrescriptionStates.DECLINED]: ColorVariant.ERROR,
  [PrescriptionStates.PENDING_VET_APPROVAL]: ColorVariant.IMPORTANT,
}

export const getOrderTableHeaderStatusColor = (
  rx: Prescription,
  prescriptionStates?: Constant[],
): KibTagThemes => {
  const stateName = getPrescriptionStateName(rx, prescriptionStates)

  if (stateName && PrescriptionStateToColorVariant[stateName]) {
    return ColorVariantToTheme[PrescriptionStateToColorVariant[stateName]]
  }

  return ColorVariantToTheme[ColorVariant.NORMAL]
}

const formatOrderTableQuantity = (
  rx: Prescription,
  parentPrescription: Prescription | undefined,
  opts: OrderTableFieldRowOptions,
): string => {
  const { prescriptionProductSizeUnit = [] } = opts
  const quantity =
    rx.totalQuantity ??
    parentPrescription?.totalQuantity ??
    getHighValue(rx.quantity) ??
    getHighValue(parentPrescription?.quantity) ??
    0
  const unitId =
    rx.totalQuantityUnitId ??
    parentPrescription?.totalQuantityUnitId ??
    undefined
  const unitName = unitId
    ? prescriptionProductSizeUnit.find((u) => u.id === unitId)?.name
    : undefined

  return unitName ? `${quantity} ${unitName}` : String(quantity)
}

interface GetOrderTableRowOptions extends OrderTableFieldRowOptions {
  fillIndex: number
  parentPrescription: Prescription | undefined
  timezone: string
  totalFills: number
}

const getOrderTableRow = (
  rx: Prescription,
  options: GetOrderTableRowOptions,
): OrderTableRow[] => {
  const { fillIndex, parentPrescription, timezone, totalFills, ...opts } =
    options
  const expirationDate = PrescriptionFields.expirationDate(rx)
  const fillStatus = getOrderFillStatusText(rx)

  return [
    {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ORDER_DATE'),
      value: rx.creationDate ? DateFormatters.dateTime(rx.creationDate) : '',
      prescription: rx,
      parentPrescription,
      relativeTime: computeRelativeTime(rx.creationDate, timezone),
    },
    {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.QTY_REQUESTED'),
      value: formatOrderTableQuantity(rx, parentPrescription, opts),
      prescription: rx,
      parentPrescription,
    },
    {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_NUMBER'),
      value: `${fillIndex} / ${totalFills}`,
    },
    {
      label: i18n.t('Common:DRUG_EXPIRATION'),
      value: DateFormatters.date(expirationDate),
    },
    {
      label: i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.FILL_STATUS'),
      value: fillStatus,
    },
  ]
}

const getCardHeaderForPrescription = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
): string | null => {
  if (rx.type === OrderType.INVENTORY) {
    return i18n.t('Common:DISPENSED_FROM_INVENTORY')
  }

  if (prescriptionType === PrescriptionType.CHEWY) {
    return i18n.t('Common:CHEWY_COM')
  }

  if (prescriptionType === PrescriptionType.IN_HOUSE) {
    return rx.origin === RxPrescriptionOrigin.Vet
      ? i18n.t('Clients:TIMELINE.RX_STATUS_TRACKER.ADMINISTERED_IN_HOUSE')
      : i18n.t('Common:DISPENSED_FROM_INVENTORY')
  }

  return null
}

export const getOrderTableSubHeader = getCardHeaderForPrescription

export interface OrderTableContent {
  cardHeaders: string[]
  fillStatus: string | null
  headers: string[]
  rows: OrderTableRow[][]
}

interface GetOrderTableFieldsOptions extends OrderTableFieldRowOptions {
  timezone: string
}

export const getOrderTableFields = (
  rx: Prescription,
  prescriptionType: NullablePrescriptionType,
  options: GetOrderTableFieldsOptions,
): OrderTableContent | null => {
  const isOrderWorkflow = rx.type === OrderType.INVENTORY
  const isChewy = prescriptionType === PrescriptionType.CHEWY
  const isInHouse = prescriptionType === PrescriptionType.IN_HOUSE

  if (!isOrderWorkflow && !isChewy && !isInHouse) return null

  const { timezone, ...opts } = options
  const refills = rx.refills ?? []
  const totalFills = refills.length + 1

  const baseRowOptions: Omit<
    GetOrderTableRowOptions,
    'fillIndex' | 'parentPrescription'
  > = {
    ...opts,
    timezone,
    totalFills,
  }

  const firstRow = getOrderTableRow(rx, {
    ...baseRowOptions,
    fillIndex: 1,
    parentPrescription: undefined,
  })

  const rows: OrderTableRow[][] = [
    firstRow,
    ...refills.map((refill, idx) =>
      getOrderTableRow(refill, {
        ...baseRowOptions,
        fillIndex: idx + 2,
        parentPrescription: rx,
      }),
    ),
  ]

  const headers = firstRow.map((field) => field.label)
  const fillStatus = getPrescriptionStateName(rx, opts.prescriptionStates)
  const parentHeader = getCardHeaderForPrescription(rx, prescriptionType)

  const cardHeaders = rows.map((row, rowIndex) => {
    if (rowIndex === 0) {
      return parentHeader || i18n.t('Common:ORDER')
    }

    const refill = refills[rowIndex - 1]
    if (!refill) {
      return parentHeader || i18n.t('Common:ORDER')
    }

    const refillHeader = getCardHeaderForPrescription(
      refill,
      refill.prescriptionType,
    )

    const dispensedFromInventory = i18n.t('Common:DISPENSED_FROM_INVENTORY')
    if (refillHeader === dispensedFromInventory) {
      return i18n.t('Clients:TIMELINE.PRESCRIPTION_CARD.REFILL_DISPENSED')
    }

    return refillHeader || parentHeader || i18n.t('Common:ORDER')
  })

  return {
    cardHeaders,
    fillStatus,
    headers,
    rows,
  }
}
